/**
 * Workspace navigation — V98-3-1 (PRD G-1, G-2, G-8 · D-98-1, D-98-7, D-98-15).
 *
 * One viewer-scoped shell, no role switcher and no layout preference:
 *   - assessor sessions keep ASSESSOR_NAV_GROUPS (Track 6 replaces it);
 *   - a viewer whose only hats are owner-type gets MINIMAL_NAV (My items ·
 *     Settings; tab bar Today · More);
 *   - everyone else gets the program spine (Today · Programs · Systems …).
 *
 * The Classic / Lifecycle layouts and the `nav_preference` toggle are
 * retired. The `workspace_settings.nav_preference` column (migration 118)
 * is left in place and simply no longer read or written by the SPA —
 * dropping it would need a table-swap migration for no user value.
 *
 * Data: GET /programs (program rows + stage chips), GET /today (the
 * viewer's hats), GET /me/counts (the shared counter behind the Today
 * badge — the same numbers Today and the program pages show).
 */
import { useQuery } from '@tanstack/react-query';
import { getMyCounts, getToday, listPrograms } from './api';
import { queryKeys } from './query-keys';
import { ASSESSOR_NAV_GROUPS, MINIMAL_NAV, isOwnerOnlyViewer, workspaceNav, type NavGroup, type NavTopLevel, type ProgramNavSource } from './nav-config';
import type { WorkspaceRole } from './workspace';

export type ShellKind = 'assessor' | 'minimal' | 'full';

export function navigationGroups(kind: ShellKind, programs: ProgramNavSource[]): (NavGroup | NavTopLevel)[] {
  if (kind === 'assessor') return ASSESSOR_NAV_GROUPS;
  if (kind === 'minimal') return MINIMAL_NAV;
  return workspaceNav(programs);
}

/** Programs list (shared cache with the program pages). */
export function usePrograms(slug: string | undefined) {
  return useQuery({ queryKey: queryKeys.programs(slug ?? ''), queryFn: () => listPrograms(slug!), enabled: !!slug, staleTime: 60_000 });
}

/** The viewer-scoped shell for this workspace. */
export function useWorkspaceNavigation(slug: string | undefined, role: WorkspaceRole | undefined) {
  const assessor = role === 'assessor';
  const enabled = !!slug && !assessor;
  const programs = usePrograms(enabled ? slug : undefined);
  const today = useQuery({ queryKey: queryKeys.today(slug ?? ''), queryFn: () => getToday(slug!), enabled, staleTime: 60_000 });
  const counts = useQuery({ queryKey: queryKeys.meCounts(slug ?? ''), queryFn: () => getMyCounts(slug!), enabled, staleTime: 30_000 });
  // Owners / admins are never minimal, so they skip waiting on /today.
  const kind: ShellKind = assessor ? 'assessor'
    : role !== 'owner' && role !== 'admin' && isOwnerOnlyViewer(today.data?.viewer) ? 'minimal' : 'full';
  return {
    kind,
    groups: navigationGroups(kind, programs.data?.programs ?? []),
    /** Owed by the viewer (shared counter) — the Today / My items badge. */
    owed: counts.data?.yours.owed ?? null,
    programsLoading: programs.isLoading,
  };
}
